import { useEffect, useState } from 'react';
import { axiosInstance } from '@api/client';
import { createAuthenticateApiPath, createFetchMaintenanceMessagePath } from '@routing/apiUrls';
import { createStorageKey, getLocalStorageItem, local, setLocalStorageItem } from '@utils/storage';
import { StorageKeys } from '@constants/storageKeys';
import useAuthentication from '@hooks/useAuthentication';
import axios, { AxiosError } from 'axios';
import { AuthenticationState } from '@js/store/authenticationAtoms';

type ErrorCallback = ((error: AxiosError) => void) | null;

let isRefreshing = false;
let errorCallback: ErrorCallback = null;

export const setAxiosErrorCallback = (cb: ErrorCallback) => {
    errorCallback = cb;
};

const isCachedLookupPath = (path?: string) => {
    return path?.includes('.blob.core.windows.net');
};

const isExternalPath = (path?: string) => {
    return path?.startsWith('http');
};

const useAxiosInterceptor = () => {
    const [ready, setReady] = useState(false);
    const { getAccessToken, refreshAccessToken, clearAccessToken, getDecodedAccessToken } = useAuthentication();

    useEffect(() => {
        // Request interceptor
        const requestInterceptor = axiosInstance.interceptors.request.use(
            (config) => {
                if (
                    config.url === createAuthenticateApiPath() ||
                    config.url === createFetchMaintenanceMessagePath() ||
                    isCachedLookupPath(config.url) ||
                    isExternalPath(config.url)
                ) {
                    return config;
                }

                const token = getAccessToken();

                if (!token) {
                    clearAccessToken(AuthenticationState.NotLoggedIn);
                    throw new axios.Cancel('Operation canceled by the user.');
                }

                const baseUrl = getLocalStorageItem<string>(createStorageKey([StorageKeys.ApiBaseUrl]));

                if (baseUrl) {
                    config.baseURL = baseUrl;
                } else {
                    console.warn(`No apiBaseUrl found for tenant ${local.getItem(StorageKeys.TenantId)}. Fallback.`);

                    const decodedToken = getDecodedAccessToken();

                    if (!decodedToken?.enrichurl) {
                        console.error(`No enrichurl found... ${JSON.stringify(decodedToken)}`);
                        return config;
                    }

                    console.warn('ApiBaseUrl found in token. Save it in localStorage.');
                    setLocalStorageItem(createStorageKey([StorageKeys.ApiBaseUrl]), decodedToken.enrichurl);
                    config.baseURL = decodedToken.enrichurl;
                }

                config.headers.Authorization = `Bearer ${token}`;

                return config;
            },
            (error) => {
                if (errorCallback) {
                    setAxiosErrorCallback(error);
                }
                return Promise.reject(error);
            },
        );

        // Response interceptor
        const responseInterceptor = axiosInstance.interceptors.response.use(
            (response) => {
                return response;
            },
            async (error) => {
                if (error?.response?.status === 401) {
                    if (!isRefreshing) {
                        isRefreshing = true;

                        const newToken = await refreshAccessToken();
                        isRefreshing = false;

                        if (newToken) {
                            error.config.headers['Authorization'] = `Bearer ${newToken}`;
                            return axiosInstance(error.config);
                        } else {
                            clearAccessToken(AuthenticationState.SessionExpired);
                        }
                    }
                } else if (error?.response?.status >= 400 && errorCallback) {
                    errorCallback(error);
                }
                return Promise.reject(error);
            },
        );

        setReady(true);

        return () => {
            axiosInstance.interceptors.request.eject(requestInterceptor);
            axiosInstance.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    return ready;
};

export default useAxiosInterceptor;
